/**
 * ThemeContext — única fonte da verdade para o tema ativo da aplicação.
 *
 * Responsabilidades:
 *  1. Lê profile.themePreference via useAuth.
 *  2. Injeta data-theme no <html> para ativar as variáveis CSS do Tailwind v4.
 *  3. Expõe useTheme() para que qualquer componente consuma o tema sem prop-drilling.
 *  4. Deixa o preview da loja (ThemePreviewContext) mandar por cima, quando ativo.
 *
 * Posicionamento na árvore: dentro de <AuthProvider> e de <ThemePreviewProvider>,
 * antes de qualquer componente que precise de theming (inserido no App.tsx).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { getThemeStyleType, type ThemeStyleType } from "../config/themes";
import { resolveEquippedThemeId } from "../lib/theme/equippedTheme";
import {
  clearBootTheme,
  writeBootSession,
  writeBootTheme,
  writeBootTokens,
} from "../lib/theme/themeBootCache";
import { applyRuntimeTokens } from "../lib/theme/runtimeTokens";
import { useAdminThemes } from "../lib/theme/useAdminThemes";
import { useAuth } from "../hooks/useAuth";
import { useThemePreview } from "./ThemePreviewContext";

// ---------------------------------------------------------------------------
// Contrato público
// ---------------------------------------------------------------------------

interface ThemeContextData {
  /** ID do tema ativo — ex: "default" | "midnight" | "cerejeira" | "matcha" | "noir" */
  theme: string;
  /** Estrutura visual: vidro neon (Obsidian Pulse) vs sólido. */
  styleType: ThemeStyleType;
  /** `theme` vem de um test-drive da loja, não do perfil gravado. */
  isPreviewing: boolean;
  /** Tema que voltaria a valer se o preview terminasse agora. */
  equippedTheme: string;
}

// ---------------------------------------------------------------------------
// Contexto interno
// ---------------------------------------------------------------------------

const ThemeContext = createContext<ThemeContextData>({
  theme: "default",
  styleType: "standard",
  isPreviewing: false,
  equippedTheme: "default",
});

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { profile, effectiveIsPro, user, isLoading } = useAuth();
  const { previewThemeId } = useThemePreview();

  // Aliases legados + guards de PRO/coin-gate moraram aqui até 04/08. Saíram
  // para `lib/theme/equippedTheme` porque o perfil público precisa da MESMA
  // resposta — enquanto a regra era local, o público lia `themeId` cru e
  // mostrava outro tema. Aqui só `themePreference` entra: no aparelho do dono
  // o espelho `themeId` não tem voz.

  // Temas criados no painel. A MESMA lista que o perfil público usa (cache de
  // módulo em `useAdminThemes`): as duas superfícies têm de chegar na mesma
  // resposta, senão o dono vê um tema que a visita não vê (BUG-80).
  const adminThemes = useAdminThemes();

  /**
   * Tokens do tema RESOLVIDO — tema do painel (`adminThemes.byId`) ou o
   * sentinela `"current_reading"` do F2 (`users.currentReadingTheme`, gerado
   * da capa da leitura atual). Local a este arquivo de propósito: os outros
   * dois consumidores (`usePublicProfileState.ts`,
   * `usePublicReadingPageState.ts`) têm a MESMA lógica, cada um com sua
   * própria cópia — ver `docs/systems/reading-theme.md` §3 pro porquê de não
   * virar um hook compartilhado.
   */
  const tokensForTheme = useCallback(
    (id: string): Record<string, string> | null =>
      id === "current_reading"
        ? profile?.currentReadingTheme?.tokens ?? null
        : adminThemes.byId[id]?.tokens ?? null,
    [profile, adminThemes.byId],
  );

  const equippedTheme = useMemo(
    () =>
      resolveEquippedThemeId({
        themePreference: profile?.themePreference,
        unlockedThemes: profile?.unlockedThemes,
        themeBeforeEvent: profile?.themeBeforeEvent,
        isPro: effectiveIsPro,
        isFounder: profile?.legacyTags?.includes("founder") === true,
        badges: profile?.badges,
        adminThemeIds: adminThemes.ids,
      }),
    [
      profile?.themePreference,
      profile?.unlockedThemes,
      profile?.themeBeforeEvent,
      effectiveIsPro,
      profile?.legacyTags,
      profile?.badges,
      adminThemes.ids,
    ],
  );

  // Preview da loja manda por cima — inclusive dos guards acima, que é
  // justamente o ponto: testar o tema que você AINDA NÃO tem. O id já vem
  // validado contra o registry pelo ThemePreviewContext, e nada disso toca
  // `themePreference` no Firestore.
  const theme = previewThemeId ?? equippedTheme;

  // Injeta data-theme no <html> — ativa as variáveis CSS do Tailwind v4
  //
  // ⚠️ `theme === "default"` tem DOIS significados, e tratá-los igual é o bug.
  // Ele é tanto "a pessoa usa o Linho" quanto "ainda não sei quem é a pessoa":
  // durante o boot não há `profile`, e `resolveEquippedThemeId` cai no default
  // por falta de dado — não por escolha de ninguém. Removendo o atributo aí, o
  // efeito APAGAVA o tema que o `theme-boot.js` acabara de pintar e devolvia o
  // Linho (papel `#f4f1ec`, quase branco) até o Firestore responder.
  //
  // Sobre um tema escuro isso não é uma nuance de tom: é a tela inteira
  // clareando e voltando — o flash que este par de arquivos existe para
  // eliminar, reintroduzido no meio do caminho e invertido (tema → Linho →
  // tema). Enquanto a sessão carrega, o que já está pintado FICA: a janela
  // mostra o tema anterior ou o novo, nunca um fundo não-temado.
  useEffect(() => {
    const html = document.documentElement;
    // Tokens do painel PRIMEIRO, e sempre — inclusive `null`, que é o que
    // limpa. Um tema do código depois de um do painel precisa que os inline
    // tenham saído, senão ele herda o papel do outro e o app fica numa mistura
    // que não existe no catálogo. `applyRuntimeTokens` limpa o que aplicou.
    applyRuntimeTokens(tokensForTheme(theme));

    if (theme !== "default") {
      html.setAttribute("data-theme", theme);
      return;
    }
    if (isLoading) return;
    html.removeAttribute("data-theme");
  }, [theme, isLoading, tokensForTheme]);

  /**
   * Semeia o tema da PRÓXIMA abertura (lido por `public/theme-boot.js` antes
   * do React montar). Sem isto o script lê uma chave que ninguém escreve e o
   * fix inteiro é código morto — que era o estado até aqui.
   *
   * Três regras, e cada uma corresponde a um modo de falha real:
   *  · `isLoading` → não grava. O resolvedor devolve "default" por falta de
   *    `profile`, e gravar apagaria a semente boa logo na abertura, todo boot.
   *  · sem usuário → limpa. A `/` é Linho por definição; sem isto quem desloga
   *    faz a landing abrir no tema do último usuário e clarear ao montar.
   *  · grava `equippedTheme`, nunca `theme`. O segundo inclui o preview da
   *    loja, e persistir test-drive faria o app abrir vestido de um tema que a
   *    pessoa não comprou.
   */
  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      clearBootTheme();
      // BUG-211: sem sessão a `/` É a landing — a flag tem que sair junto,
      // senão a landing abriria escondida até o React montar.
      writeBootSession(false);
      return;
    }
    // BUG-211: com sessão, a próxima abertura esconde a landing pré-renderizada
    // antes do primeiro paint (lido por `public/theme-boot.js`). Vive no mesmo
    // efeito do tema porque as três regras acima (carregando não grava, sem
    // usuário apaga, com usuário grava) são exatamente as dela.
    writeBootSession(true);
    writeBootTheme(equippedTheme);
    // Os tokens vão junto, e só para tema do painel. Tema do código tem bloco
    // CSS no bundle e o `data-theme` sozinho o pinta; gravar tokens para ele
    // seria duplicar no localStorage o que já viaja compilado.
    //
    // Passar `null` para tema do código é o que APAGA os tokens do painel na
    // troca — sem isso, quem sai de um tema do admin abriria a próxima sessão
    // com os tokens antigos por cima do tema novo.
    writeBootTokens(equippedTheme, tokensForTheme(equippedTheme));
  }, [isLoading, user, equippedTheme, tokensForTheme]);

  const styleType = useMemo(() => getThemeStyleType(theme), [theme]);

  const value = useMemo<ThemeContextData>(
    () => ({ theme, styleType, isPreviewing: previewThemeId !== null, equippedTheme }),
    [theme, styleType, previewThemeId, equippedTheme],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook público
// ---------------------------------------------------------------------------

export function useTheme(): ThemeContextData {
  return useContext(ThemeContext);
}
